import type { Category } from '@/api/types'

export interface CategoryNode {
  category: Category
  children: Array<Category>
  /**
   * How deep this node sits. 0 for a main group, 1 for a subgroup.
   *
   * Anything deeper should not exist, but the builder does not assume that — see below.
   */
  depth: number
}

/**
 * Turns the flat category list into the two-level tree the Struktur tab renders.
 *
 * The API deliberately returns a flat list with `parentId` (CHG-2026-09-product-category-hierarchy,
 * D4); nesting it here keeps the response shape unchanged for its other consumers.
 *
 * **Tolerant of unexpected depth on purpose.** The two-level rule is enforced only in the
 * application layer, not in the database (D1), so a third level is possible in principle — a direct
 * SQL edit, or a future second write path. If one ever appears, a grandchild is rendered under its
 * own parent rather than dropped: an oddly placed row is a visible problem, a silently missing
 * article is not.
 *
 * A category whose parent is missing or inactive is treated as a main group, for the same reason.
 */
export function buildCategoryTree(
  categories: Array<Category>,
): Array<CategoryNode> {
  const byId = new Map(categories.map((c) => [c.id, c]))

  const childrenOf = new Map<number, Array<Category>>()
  const roots: Array<Category> = []

  for (const category of categories) {
    const parentId = category.parentId
    // A parent we cannot see (deleted, inactive, another page) makes this a root rather than an
    // orphan that never renders.
    if (parentId == null || !byId.has(parentId)) {
      roots.push(category)
      continue
    }
    const siblings = childrenOf.get(parentId)
    if (siblings) {
      siblings.push(category)
    } else {
      childrenOf.set(parentId, [category])
    }
  }

  const bySortThenName = (a: Category, b: Category) =>
    a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)

  const nodes: Array<CategoryNode> = []
  const emitted = new Set<number>()

  const emit = (category: Category, depth: number) => {
    if (emitted.has(category.id)) return
    emitted.add(category.id)
    const children = (childrenOf.get(category.id) ?? [])
      .slice()
      .sort(bySortThenName)
    nodes.push({ category, children, depth })
    for (const child of children) emit(child, depth + 1)
  }

  for (const root of roots.slice().sort(bySortThenName)) emit(root, 0)

  // Anything left over sits in a parent cycle, which the server refuses but the client must not
  // lose. Render them as main groups.
  for (const category of categories) {
    if (!emitted.has(category.id)) emit(category, 0)
  }

  return nodes
}

/**
 * The groups an article may be assigned to, in the order the Struktur tab shows them.
 *
 * Two things the flat API list does not give the assignment dropdown:
 * the collector is not a destination — assigning an article to it is what the worklist exists to
 * undo — and the list is sorted by `sortOrder` alone, which puts a subgroup wherever its own number
 * falls rather than under its main group. A dropdown ordered that way contradicts the tree next to
 * it, and "Alkoholfrei" above "Getränke" reads as two unrelated groups.
 */
export function assignableTargets(
  categories: Array<Category>,
): Array<CategoryNode> {
  return buildCategoryTree(
    categories.filter((c) => c.active && c.code !== COLLECTOR_CODE),
  )
}

/** Why a category cannot be the parent of `candidate`, or null when it can. */
export type ParentBlockReason =
  | 'self'
  | 'already-subgroup'
  | 'has-children'
  | 'collector'

/**
 * Mirrors the server's rules so the selector can disable an option and say why.
 *
 * Deliberately returns a reason rather than filtering: someone looking for "Alkoholfrei" has to
 * find it and learn why it cannot be used, instead of searching for an entry that is not there.
 * The server stays the authority — this only spares the user a round trip.
 */
export function parentBlockReason(
  candidate: Category,
  target: Category,
  all: Array<Category>,
): ParentBlockReason | null {
  if (candidate.id === target.id) return 'self'
  if (candidate.code === COLLECTOR_CODE) return 'collector'
  if (candidate.parentId != null) return 'already-subgroup'
  if (all.some((c) => c.parentId === target.id)) return 'has-children'
  return null
}

/**
 * The collector category every unassigned article lands in. It stays a top-level group with no
 * children: nested under a real group it would fold unassigned revenue into that group's numbers.
 */
export const COLLECTOR_CODE = 'general'
